import { Suspense, useEffect, useRef, useState } from 'react';
import SectionLoader from './SectionLoader';

const LazyOnView = ({ children, minHeight = '400px', rootMargin = '80px 0px' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Wait until the browser is idle (or user scrolls) before observing —
    // stops a cascade of chunk downloads right after first paint.
    let observer;
    let idleId;
    let timeoutId;
    let cancelled = false;

    let started = false;
    const observe = () => {
      if (cancelled || !node || started) return;
      started = true;
      window.removeEventListener('scroll', onScroll);
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin }
      );
      observer.observe(node);
    };

    const start = () => observe();

    const onScroll = () => {
      start();
      if (idleId != null) window.cancelIdleCallback?.(idleId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };

    window.addEventListener('scroll', onScroll, { passive: true, once: true });

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(start, { timeout: 5000 });
    } else {
      timeoutId = window.setTimeout(start, 1200);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', onScroll);
      if (idleId != null) window.cancelIdleCallback?.(idleId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [rootMargin]);

  return (
    <div ref={ref} style={{ minHeight }}>
      {visible ? (
        <Suspense fallback={<SectionLoader minHeight={minHeight} />}>
          {children}
        </Suspense>
      ) : (
        <div style={{ minHeight }} aria-hidden="true" className="bg-dark" />
      )}
    </div>
  );
};

export default LazyOnView;
